import * as React from 'react';
import * as _ from 'lodash';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import { injectIntl } from 'react-intl';
import ConfirmDialog from '../../components/Shared/ConfirmDialog';
import QueuesIndex from '../../components/Queues/index';
import * as QueuesApi from '../../api/queue';
import {
  DEFAULT_QUEUE_VIEW,
  buildQueueListQuery,
  retryErrorMessageId,
} from '../../helpers/queue-filters';

const PAGE_SIZE = 20;

class QueuesIndexContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      queues: [],
      pages: 0,
      page: 1,
      isLoading: true,
      where: {
        id: '',
        invitation_id: '',
        view: DEFAULT_QUEUE_VIEW,
        order: 'date desc',
      },
    };

    // Only the latest list request may write to the state: a slow reload must
    // not overwrite a newer filter. Kept off the state, read synchronously.
    this.latestRequestId = 0;
    // The last filters and page asked for, already set while that request is
    // in flight. Calls without filters (reload, pagination) read them instead
    // of the state, which still holds the previous filter until it lands.
    this.requestedWhere = this.state.where;
    this.requestedPage = this.state.page;

    this.onPageChange = this.onPageChange.bind(this);
    this.retry = this.retry.bind(this);
    this.destroy = this.destroy.bind(this);
  }

  async destroy(id) {
    const onConfirmClick = async () => {
      await QueuesApi.destroy(id);

      toast.success(
        this.props.intl.formatMessage({ id: 'The record was destroyed' })
      );
      this.onPageChange({ page: 1 });
    };

    confirmAlert({
      message: this.props.intl.formatMessage({
        id: 'Delete queue record? The invitation will end up without an access code and this can not be undone',
      }),
      customUI: props => <ConfirmDialog {...props} {...{ onConfirmClick }} />,
    });
  }

  retry(queue) {
    // A closed record was already handled by the cron: reopening it is
    // harmless but deliberate, so it asks first.
    if (queue.pending) {
      return this.requeue(queue.id);
    }

    confirmAlert({
      message: this.props.intl.formatMessage({
        id: 'Reopen this closed queue record? The cron will try to deliver the access code again',
      }),
      customUI: props => (
        <ConfirmDialog
          {...props}
          onConfirmClick={() => this.requeue(queue.id)}
        />
      ),
    });
  }

  async requeue(id) {
    try {
      await QueuesApi.retry(id);
    } catch (err) {
      const code = _.get(err, 'response.data.error');
      this.showRetryError(err);

      // The row is stale (gone, expired, already handled): reload it so the
      // status and the retry button reflect the backend. BUSY is transient,
      // the row is still valid, so it is kept as is.
      if (retryErrorMessageId(code) && code !== 'QUEUE_RECORD_BUSY') {
        await this.reloadQuietly();
      }
      return;
    }

    toast.success(
      this.props.intl.formatMessage({
        id: 'Queue record re-queued, the cron will try it within 1 minute',
      })
    );
    await this.reloadQuietly();
  }

  // Reloads the latest requested page with the latest requested filters.
  reload() {
    return this.onPageChange({ page: this.requestedPage });
  }

  // The retry itself is done: a failed reload must not reject it.
  async reloadQuietly() {
    try {
      await this.reload();
    } catch (reloadErr) {
      // Not propagated. catchError has toasted an HTTP error; a network error
      // (no response) makes it throw a TypeError before any toast (card #155).
    }
  }

  showRetryError(err) {
    const messageId = retryErrorMessageId(_.get(err, 'response.data.error'));

    // Unknown errors keep the generic toast already shown by catchError.
    if (!messageId) {
      return;
    }

    // catchError has just queued a toast with the raw error code; replace it
    // with the translated message (toastify handles both events in order).
    toast.dismiss();
    toast.error(this.props.intl.formatMessage({ id: messageId }));
  }

  // Public entry point (filter, pagination, reloads): the only place that
  // starts a new list request, so the only one that bumps latestRequestId.
  async onPageChange({ page, ...where }) {
    const filters = _.isEmpty(where) ? this.requestedWhere : where;
    this.requestedWhere = filters;
    this.requestedPage = page;
    this.latestRequestId += 1;

    return this.loadPage(page, filters, this.latestRequestId);
  }

  isStale(requestId) {
    return requestId !== this.latestRequestId;
  }

  async loadPage(page, filters, requestId) {
    let response;
    try {
      response = await QueuesApi.list({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        ...buildQueueListQuery(filters),
      });
    } catch (err) {
      // A superseded request resolves quietly; the current one still rejects,
      // so the Filter form can show the field errors.
      if (this.isStale(requestId)) {
        return;
      }
      // Nothing newer was asked for and this one never landed: the next
      // reload goes back to the page and filters on screen.
      this.requestedWhere = this.state.where;
      this.requestedPage = this.state.page;
      throw err;
    }

    // Checked before the fallback too: a superseded request must neither write
    // to the state nor move requestedPage.
    if (this.isStale(requestId)) {
      return;
    }

    const { data, headers } = response;
    const pages = parseInt(headers['x-total-count'], 10);

    // The page can vanish under the user (e.g. reopening the last rows of the
    // last page): fall back to the last valid one, as part of the same request
    // (same id and filters). lastPage < page on every recursion, so it always
    // terminates.
    const lastPage = Math.max(1, Math.ceil(pages / PAGE_SIZE));
    if (_.isEmpty(data) && pages > 0 && page > lastPage) {
      this.requestedPage = lastPage;
      return this.loadPage(lastPage, filters, requestId);
    }

    this.setState({
      queues: data,
      pages,
      page,
      where: filters,
    });
  }

  async componentDidMount() {
    this.onPageChange({ page: 1 });
  }

  componentWillUnmount() {
    // Turns any request still in flight stale: no setState after unmount.
    this.latestRequestId += 1;
  }

  render() {
    return (
      <QueuesIndex
        queues={this.state.queues}
        isLoading={this.state.isLoading}
        pages={this.state.pages}
        page={this.state.page}
        onPageChange={this.onPageChange}
        retry={this.retry}
        destroy={this.destroy}
      />
    );
  }
}

export default injectIntl(QueuesIndexContainer);
